import { gql, useQuery } from '@apollo/client';
import { Heading } from '@apollo/orbit';
import React from 'react';

import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { UseNewFedNextVersionAvailableFragment } from 'src/hooks/useNewFedNextVersionAvailable/useNewFedNextVersionAvailable';
import {
  ignorePermissionsErrors,
  ignorePlanErrors,
} from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { GraphRef } from '../hooks/useGraphRef';

import { BuildConfigCard } from './variantSettingsCards/BuildConfigCard';
import { ContractVariantSettingsCard } from './variantSettingsCards/ContractVariantSettingsCard';
import { DeleteVariantSettingsCard } from './variantSettingsCards/DeleteVariantSettingsCard';
import { FederationVersionInfoFragment } from './variantSettingsCards/FederationVersionInfo';
import {
  PersistedQueriesConfigCard,
  persistedQueriesConfigCardServiceFragment,
  persistedQueriesConfigCardVariantFragment,
} from './variantSettingsCards/PersistedQueriesConfigCard';
import { ProtectedVariantSettingsCard } from './variantSettingsCards/ProtectedVariantSettingsCard';
import { PublicVariantSettingsCard } from './variantSettingsCards/PublicVariantSettingsCard';

const variantGeneralSettingsPageQuery = gql`
  query VariantGeneralSettingsPageQuery($graphId: ID!, $graphVariant: String!) {
    graph: service(id: $graphId) {
      id
      title
      graphType
      roles {
        canManageBuildConfig
      }
      variant(name: $graphVariant) {
        id
        name
        isContract
        allowedTracks {
          badge
          buildPipelineTrack
          compositionVersion
          deprecatedAt
          displayName
          minimumGatewayVersion
          minimumRouterVersion
          notSupportedAt
        }
        contractFilterConfig {
          include
          exclude
          hideUnreachableTypes
        }
        isProtected
        hasManagedSubgraphs
        isPublic
        url
        activeSchemaPublish {
          id
          publishedAt
          schema {
            hash
            createdAt
          }
        }
        sourceVariant {
          id
          name
        }
        derivedVariantCount
        compositionVersion
        supportedDirectives {
          name
          enabled
        }
        buildConfigUpdatedAt
        buildConfig {
          buildPipelineTrack
          tagInApiSchema
        }
        router {
          id
          routerVersion {
            core
          }
        }
        ...UseNewFedNextVersionAvailableFragment
        ...FederationVersionInfoFragment
        ...PersistedQueriesConfigCardVariantFragment
      }
      graphCapabilities {
        minimumBuildPipelineTrack
      }
      ...PersistedQueriesConfigCardServiceFragment
    }
  }
  ${persistedQueriesConfigCardVariantFragment}
  ${persistedQueriesConfigCardServiceFragment}
  ${FederationVersionInfoFragment}
  ${UseNewFedNextVersionAvailableFragment}
`;

export const VariantGeneralSettingsPage = ({
  graphRef,
}: {
  graphRef: GraphRef;
}) => {
  const { graphId, graphVariant } = graphRef;
  const { data, loading, error, refetch } = useQuery<
    GraphQLTypes.VariantGeneralSettingsPageQuery,
    GraphQLTypes.VariantGeneralSettingsPageQueryVariables
  >(variantGeneralSettingsPageQuery, {
    variables: {
      graphId,
      graphVariant,
    },
    context: appLinkContext({
      catchErrors: [ignorePermissionsErrors, ignorePlanErrors],
    }),
  });
  const [currentAccountId] = useCurrentAccountId();
  const currentPlan = useCurrentPlan({
    skip: !currentAccountId,
    accountId: currentAccountId as string,
  });

  const currentGraph = data?.graph;
  const currentVariant = data?.graph?.variant;
  const routerVersion = currentVariant?.router?.routerVersion?.core;

  return (
    <>
      {loading || currentPlan.loading ? (
        <Loading />
      ) : error || currentPlan.error ? (
        <ErrorMessage error={error || currentPlan.error} />
      ) : !currentGraph || !currentVariant ? (
        <NotFound />
      ) : (
        data && (
          <>
            {currentVariant && currentVariant.isContract ? (
              <ContractVariantSettingsCard data={data} />
            ) : (
              <BuildConfigCard
                isGraph={false}
                serviceId={graphId}
                variant={currentVariant}
                canManageBuildConfig={Boolean(
                  currentGraph.roles?.canManageBuildConfig,
                )}
                graphType={currentGraph.graphType}
                refetchData={refetch}
                routerVersion={routerVersion}
              />
            )}

            {currentGraph.graphType !==
              GraphQLTypes.GraphType.CLOUD_SUPERGRAPH && (
              <PersistedQueriesConfigCard
                settingsLevel="variant"
                graph={currentGraph}
                graphVariant={currentVariant}
              />
            )}

            <Heading className="pt-4" variant="heading" size="2xl">
              🚧 Danger zone
            </Heading>
            <ProtectedVariantSettingsCard
              data={data}
              isProtected={currentVariant?.isProtected || false}
              hasUserRoleCapabilities={
                currentPlan.currentPlan?.userRoles || false
              }
              isEnterprise={
                currentPlan.currentPlan?.kind?.includes('ENTERPRISE') || false
              }
            />
            <PublicVariantSettingsCard
              data={data}
              isPublic={currentVariant?.isPublic || false}
            />
            <DeleteVariantSettingsCard data={data} />
          </>
        )
      )}
    </>
  );
};
